/**
 * Authentication Context Provider
 * Manages authentication state, tokens, and user profile
 */

import { createContext, useContext, useState, useEffect, useCallback, useRef, type ReactNode } from 'react'
import { env } from '@/config/env'
import { createApiClient, setApiClient } from '@/services/api'
import type { AuthState, AuthTokens, UserProfile } from '@/types/auth'
import * as authService from '@/services/authService'

interface AuthContextType extends AuthState {
  login: () => void
  logout: () => void
  refreshTokens: () => Promise<boolean>
  setTokens: (tokens: AuthTokens, user: UserProfile) => void
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

interface AuthProviderProps {
  children: ReactNode
}

export function AuthProvider({ children }: AuthProviderProps) {
  // Initialize synchronously from localStorage so ProtectedRoute never blocks on first render.
  // This eliminates the blank-screen / spinner phase on page load for returning users.
  const [state, setState] = useState<AuthState>(() => {
    // Auth bypass (dev/staging) — always authenticated
    if (env.authBypassEnabled) {
      const { tokens: mockTokens, user: mockUser } = authService.getMockAuth()
      return {
        isAuthenticated: true,
        user: mockUser,
        tokens: {
          accessToken: mockTokens.access_token,
          idToken: mockTokens.id_token,
          refreshToken: mockTokens.refresh_token,
          expiresAt: Date.now() + mockTokens.expires_in * 1000,
        },
        isLoading: false,
        error: null,
      }
    }
    // Returning user with valid stored token
    try {
      const storedTokens = localStorage.getItem('auth_tokens')
      const storedUser = localStorage.getItem('auth_user')
      if (storedTokens && storedUser) {
        const tokens: AuthTokens = JSON.parse(storedTokens)
        const user: UserProfile = JSON.parse(storedUser)
        if (tokens.expiresAt > Date.now()) {
          return { isAuthenticated: true, user, tokens, isLoading: false, error: null }
        }
        // Expired — clean up
        localStorage.removeItem('auth_tokens')
        localStorage.removeItem('auth_user')
      }
    } catch {
      localStorage.removeItem('auth_tokens')
      localStorage.removeItem('auth_user')
    }
    // No valid session — not loading (just not authenticated)
    return { isAuthenticated: false, user: null, tokens: null, isLoading: false, error: null }
  })

  // Use ref to avoid closure issues with token access
  const tokensRef = useRef<AuthTokens | null>(null)
  const isAuthenticatedRef = useRef<boolean>(false)

  // Update refs whenever state changes
  useEffect(() => {
    tokensRef.current = state.tokens
    isAuthenticatedRef.current = state.isAuthenticated
  }, [state.tokens, state.isAuthenticated])

  // Get access token for API client - uses ref to always get current token
  const getAccessToken = useCallback(() => {
    return tokensRef.current?.accessToken || null
  }, [])

  // Initialize API client with auth - only once since getAccessToken doesn't change
  useEffect(() => {
    const client = createApiClient(getAccessToken)
    setApiClient(client)
  }, [getAccessToken])

  // Handle auth expiration events
  useEffect(() => {
    const handleAuthExpired = () => {
      setState(prev => ({
        ...prev,
        isAuthenticated: false,
        tokens: null,
        error: 'Your session has expired. Please log in again.',
      }))
    }

    window.addEventListener('auth:expired', handleAuthExpired)
    return () => window.removeEventListener('auth:expired', handleAuthExpired)
  }, [])

  // Auth is now initialized synchronously in useState above.
  // This effect is kept only as a no-op placeholder in case async token
  // validation (e.g. Cognito refresh) is added in the future.
  useEffect(() => {
    // Nothing to do — state is already set synchronously at init time.
  }, [])

  const login = useCallback(() => {
    if (env.authBypassEnabled) {
      // Already logged in with mock auth
      return
    }
    
    // Redirect to Cognito hosted UI
    window.location.href = authService.buildLoginUrl()
  }, [])

  const logout = useCallback(() => {
    // Clear localStorage
    localStorage.removeItem('auth_tokens')
    localStorage.removeItem('auth_user')
    
    setState({
      isAuthenticated: false,
      user: null,
      tokens: null,
      isLoading: false,
      error: null,
    })

    if (env.authBypassEnabled) {
      // Just clear state for mock auth
      return
    }

    // Redirect to Cognito logout
    window.location.href = authService.buildLogoutUrl()
  }, [])

  const refreshTokens = useCallback(async (): Promise<boolean> => {
    // Skip in mock auth mode
    if (env.authBypassEnabled) {
      console.log('[AuthContext] Skipping token refresh in mock auth mode')
      return true
    }

    if (!state.isAuthenticated || !state.tokens?.refreshToken) {
      console.warn('[AuthContext] Cannot refresh tokens: not authenticated or no refresh token')
      return false
    }

    try {
      const response = await authService.refreshAccessToken(state.tokens.refreshToken)
      const expiresAt = Date.now() + response.expires_in * 1000

      const newTokens = {
        accessToken: response.access_token,
        idToken: response.id_token,
        refreshToken: state.tokens.refreshToken || response.refresh_token,
        expiresAt,
      }

      // Persist updated tokens
      try {
        localStorage.setItem('auth_tokens', JSON.stringify(newTokens))
      } catch (error) {
        console.error('[AuthContext] Failed to persist refreshed tokens:', error)
      }

      setState(prev => ({
        ...prev,
        tokens: newTokens,
        error: null,
      }))

      return true
    } catch (error) {
      console.error('Token refresh failed:', error)
      
      // Clear invalid tokens
      localStorage.removeItem('auth_tokens')
      localStorage.removeItem('auth_user')
      
      setState(prev => ({
        ...prev,
        isAuthenticated: false,
        tokens: null,
        error: 'Session expired. Please log in again.',
      }))
      return false
    }
  }, [state.tokens, state.isAuthenticated])

  const setTokens = useCallback((tokens: AuthTokens, user: UserProfile) => {
    // Persist to localStorage
    try {
      localStorage.setItem('auth_tokens', JSON.stringify(tokens))
      localStorage.setItem('auth_user', JSON.stringify(user))
    } catch (error) {
      console.error('[AuthContext] Failed to persist tokens:', error)
    }
    
    setState({
      isAuthenticated: true,
      user,
      tokens,
      isLoading: false,
      error: null,
    })
  }, [])

  // Set up automatic token refresh
  useEffect(() => {
    if (!state.tokens || !state.isAuthenticated) return

    // Skip token refresh in mock auth mode
    if (env.authBypassEnabled) {
      return
    }

    const timeUntilExpiry = state.tokens.expiresAt - Date.now()
    const refreshTime = timeUntilExpiry - 5 * 60 * 1000 // Refresh 5 minutes before expiry

    if (refreshTime <= 0) {
      // Token already expired - clear auth state instead of trying to refresh
      console.warn('[AuthContext] Token expired, clearing auth state')
      localStorage.removeItem('auth_tokens')
      localStorage.removeItem('auth_user')
      setState(prev => ({
        ...prev,
        isAuthenticated: false,
        tokens: null,
        error: 'Session expired. Please log in again.',
      }))
      return
    }

    const timer = setTimeout(() => {
      // Double-check authentication state before refreshing using ref
      // This prevents refresh attempts after logout
      if (isAuthenticatedRef.current && tokensRef.current?.refreshToken) {
        refreshTokens()
      } else {
        console.warn('[AuthContext] Skipping token refresh - user not authenticated')
      }
    }, refreshTime)

    return () => clearTimeout(timer)
  }, [state.tokens, state.isAuthenticated, refreshTokens])

  const value: AuthContextType = {
    ...state,
    login,
    logout,
    refreshTokens,
    setTokens,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider')
  }
  return context
}
